import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useApi, useTenant } from '@/context';
import type { SearchDatasetsParams } from '@/services';

type SearchKey = Omit<SearchDatasetsParams, 'cursor'>;

// Tenant-scoped, so the tenant is part of the cache key — see caseKeys.
export const datasetKeys = {
    all: (tenantId: string) => ['datasets', tenantId] as const,
    search: (tenantId: string, params: SearchKey) =>
        [...datasetKeys.all(tenantId), 'search', params] as const,
    detail: (tenantId: string, id: string) => [...datasetKeys.all(tenantId), 'detail', id] as const,
    preview: (tenantId: string, id: string) => [...datasetKeys.all(tenantId), 'preview', id] as const,
};

/**
 * Cursor-paginated infinite query for dataset search.
 *
 * IMPORTANT: memoize the `params` object at the call site (e.g. via
 * `useMemo`) — otherwise every render produces a new query key and
 * the hook re-fetches in a loop.
 */
export function useDatasetSearch(params: SearchKey) {
    const { inputDatasets } = useApi();
    const { tenantId } = useTenant();
    return useInfiniteQuery({
        queryKey: datasetKeys.search(tenantId!, params),
        queryFn: ({ pageParam }) => inputDatasets.searchDatasets({ ...params, cursor: pageParam }),
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        initialPageParam: undefined as string | undefined,
        enabled: !!tenantId,
    });
}

export function useDataset(id: string | undefined) {
    const { inputDatasets } = useApi();
    const { tenantId } = useTenant();
    return useQuery({
        queryKey: datasetKeys.detail(tenantId!, id ?? ''),
        queryFn: () => inputDatasets.getDataset(id!),
        enabled: !!id && !!tenantId,
    });
}

/**
 * Preview blob cached 10 min. Re-selecting a row in the picker is
 * instant after the first fetch.
 */
export function useDatasetPreview(id: string | undefined) {
    const { inputDatasets } = useApi();
    const { tenantId } = useTenant();
    return useQuery({
        queryKey: datasetKeys.preview(tenantId!, id ?? ''),
        queryFn: () => inputDatasets.getPreview(id!),
        enabled: !!id && !!tenantId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });
}
